/* eslint-disable no-unused-vars */
/** @jsx jsx */
import React, { useState, useContext } from 'react';
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';

import {
    isWithinNBusinessDays,
    isWithinNCalendarDays,
    checkTodayDateAfterGiven
} from 'school-space-utils/date';

import {
    Button,
    Card,
    Divider,
    Icon,
    Link,
    Property,
    Spacer,
    BookingStatus,
    ModalContext,
    SpaceBookingsTable
} from 'school-space-client/components';
import { cancelCustomerBooking, cancelCustomerSpaceBooking } from 'school-space-client/api';

import css from './css';

const BookingCard = ({ booking, onUpdate, isExpanded, holidays }) => {
    const [expanded, setExpanded] = useState(isExpanded || false);
    const {
        openModal,
        closeModal,
        types: { cancellation, confirmation, information, spaceBooking }
    } = useContext(ModalContext);

    const {
        id: bookingId,
        recurringBookingId,
        customer: { id: customerId },
        date,
        dateString,
        location,
        school,
        site,
        spaces,
        state
    } = booking;

    const bookingIsCancelled = state.id === 'cancelled';
    const showBookingActions = !bookingIsCancelled;

    const isAmendable = showBookingActions && !isWithinNBusinessDays({ n: 5, date, holidays });
    const isCancellable = showBookingActions && !isWithinNCalendarDays({ n: 2, date });

    const CancelConfirmationMessage = () => (
        <React.Fragment>
            <p>
                Once cancelled, {spaces.length > 1 ? 'these spaces are' : 'this space is'} no longer
                reserved and will be available for other customers to book.
            </p>
            <p>
                Please be aware that you may be subject to the following{' '}
                <Link
                    url="https://school-space.org/terms-conditions/school-space-cancellation-policy"
                    isExternal
                    opensInNewTab
                >
                    cancellations fees
                </Link>
                .
            </p>
        </React.Fragment>
    );

    const SpaceCancelConfirmationMessage = () => (
        <React.Fragment>
            <p>
                Once removed from your booking, this space is no longer reserved and will be
                available for other customers to book.
            </p>
            <p>
                Please be aware that you may be subject to the following{' '}
                <Link
                    url="https://school-space.org/terms-conditions/school-space-cancellation-policy"
                    isExternal
                    opensInNewTab
                >
                    cancellations fees
                </Link>
                .
            </p>
        </React.Fragment>
    );

    const spaceCancellationConfirmationMessage = () => (
        <React.Fragment>
            <p>
                As this cancellation is after the 1st of the month for the current month, you will
                still be charged for this booking.
            </p>
            <p>
                Please see our cancellation policy for more information.{' '}
                <Link
                    url="https://school-space.org/terms-conditions/school-space-cancellation-policy"
                    isExternal
                    opensInNewTab
                >
                    cancellations fees
                </Link>
                .
            </p>
        </React.Fragment>
    );

    const BookingUncancellableMessage = () => (
        <React.Fragment>
            <p>
                This booking is too soon to be cancelled online. Please{' '}
                <Link url="https://school-space.org/about-us/contact-us/" isExternal opensInNewTab>
                    get in touch
                </Link>{' '}
                if you cannot attend the booking.
            </p>
        </React.Fragment>
    );

    const ClosedMessage = () => (
        <React.Fragment>
            <p>
                We are currently closed over the festive period, so are unable to process
                amendments.
            </p>
            <p>Please come back after the 6th of January and try again.</p>
        </React.Fragment>
    );

    const BookingUnamendableMessage = () => (
        <React.Fragment>
            <p>
                This booking is too soon to be amended online. Please{' '}
                <Link url="https://school-space.org/about-us/contact-us/" isExternal opensInNewTab>
                    get in touch
                </Link>{' '}
                if you need to amend this booking.
            </p>
        </React.Fragment>
    );

    const openBookingCancellationModal = () => {
        openModal({
            type: cancellation,
            props: {
                cancellationType: 'updatedCustomerBooking', // recurringBookingId ? 'customerBooking' : 'customerOneOff',
                confirmAction: cancellationReason =>
                    cancelCustomerBooking(customerId, bookingId, cancellationReason)
                        .then(onUpdate)
                        .then(closeModal),
                title: 'Are you sure you want to cancel this booking?',
                message: CancelConfirmationMessage,
                confirmText: 'Yes, cancel booking',
                cancelText: 'No, keep booking'
            }
        });
    };

    const openSpaceCancellationModal = ({ id: spaceBookingId }) => {
        openModal({
            type: confirmation,
            props: {
                confirmAction: () =>
                    cancelCustomerSpaceBooking(customerId, bookingId, spaceBookingId)
                        .then(onUpdate)
                        .then(closeModal),
                title: 'Are you sure you want to remove this space?',
                confirmText: 'Yes, remove space',
                confirmButtonState: 'warning',
                cancelText: 'No, keep space',
                message: SpaceCancelConfirmationMessage
            }
        });
    };

    const checkSpaceCancellationModal = ({ id }) => {
        if (checkTodayDateAfterGiven(date)) {
            openModal({
                type: confirmation,
                props: {
                    confirmAction: () => openSpaceCancellationModal({ id }),
                    title: 'Please confirm if you wish to cancel the booking',
                    confirmText: 'Yes, Proceed',
                    confirmButtonState: 'warning',
                    cancelText: 'No, Cancel',
                    message: spaceCancellationConfirmationMessage
                }
            });
        } else {
            openSpaceCancellationModal({ id });
        }
    };

    const checkBookingCancellationModal = () => {
        if (checkTodayDateAfterGiven(date)) {
            openModal({
                type: confirmation,
                props: {
                    confirmAction: () => openBookingCancellationModal(),
                    title: 'Please confirm if you wish to cancel the booking',
                    confirmText: 'Yes, Proceed',
                    confirmButtonState: 'warning',
                    cancelText: 'No, Cancel',
                    message: spaceCancellationConfirmationMessage
                }
            });
        } else {
            openBookingCancellationModal();
        }
    };

    const openSpaceBookingModal = originalSpaceBooking =>
        openModal({
            type: spaceBooking,
            props: {
                date: booking.date,
                dateString,
                school,
                site,
                bookingId,
                customerId,
                onUpdate,
                originalSpaceBooking
            }
        });

    const openUncancellableModal = () => {
        openModal({
            type: information,
            props: {
                title: 'Booking can no longer be cancelled',
                cancelText: 'Close',
                hideButton: true,
                message: BookingUncancellableMessage
            }
        });
    };

    const openUnamendableModal = () => {
        openModal({
            type: information,
            props: {
                title: 'Booking can no longer be amended',
                cancelText: 'Close',
                hideButton: true,
                message: BookingUnamendableMessage
            }
        });
    };

    return (
        <Card styles={css.wrapper}>
            <div css={css.header}>
                <div css={css.propertyRow}>
                    <Property title="Date" styles={css.property}>
                        {dateString}
                    </Property>
                    <Property title="Location" styles={css.property}>
                        {location}
                    </Property>
                </div>
                <div css={css.meta}>
                    {bookingIsCancelled && <BookingStatus state={state.id} />}
                    <Icon
                        styles={css.arrow({ rotated: expanded })}
                        name="arrow"
                        ariaExpanded={expanded}
                        ariaLabel="toggle booking card"
                        onClick={() => setExpanded(!expanded)}
                    />
                </div>
            </div>
            <div css={css.content({ visible: expanded })} aria-hidden={!expanded}>
                <div css={css.spaces}>
                    <SpaceBookingsTable
                        spaceBookings={spaces}
                        updateSpace={isAmendable ? openSpaceBookingModal : () => {}}
                        cancelSpace={isCancellable ? checkSpaceCancellationModal : () => {}}
                        focusable={expanded}
                    />
                </div>
                {showBookingActions && (
                    <div>
                        <Divider />
                        <Spacer />
                        <div css={css.buttonContainer}>
                            <Button
                                state="ghost-warning"
                                styles={css.actionButton}
                                size="small"
                                focusable={expanded}
                                onClick={
                                    isCancellable
                                        ? checkBookingCancellationModal
                                        : openUncancellableModal
                                }
                            >
                                Cancel booking
                            </Button>
                            <Button
                                state="ghost"
                                styles={css.actionButton}
                                size="small"
                                focusable={expanded}
                                onClick={isAmendable ? openSpaceBookingModal : openUnamendableModal}
                            >
                                Add a space
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

BookingCard.propTypes = {
    booking: PropTypes.objectOf(PropTypes.any).isRequired,
    onUpdate: PropTypes.func.isRequired,
    isExpanded: PropTypes.bool,
    holidays: PropTypes.arrayOf(PropTypes.any)
};

BookingCard.defaultProps = {
    holidays: [],
    isExpanded: false
};

export default BookingCard;
